import { Color3, FreeCamera, MeshBuilder, Quaternion, Ray, Scene, StandardMaterial, TransformNode, Vector3 } from "@babylonjs/core";
import type { GameAssetManager } from "../core/AssetManager";
import type { JungleObstacle } from "../environment/JungleGenerator";
import { ITEM_DEFINITIONS, type BaitId } from "../inventory/ItemDefinitions";
import type { PlacedBait } from "./LureSystem";

export interface PlacementResult { readonly success: boolean; readonly message: string; readonly baitId?: number; readonly type?: BaitId; }
export type BaitAvailability = Readonly<Record<BaitId, number>>;
export interface BaitWorldConfig { readonly size: number; readonly rotation: Vector3; readonly groundClearance: number; }

export const COIN_WORLD_SIZE = 0.07;
export const MIRROR_WORLD_SIZE = 0.55;
export const MIRROR_GROUND_CLEARANCE = 0.005;
export const FAKE_BLOOD_GROUND_CLEARANCE = 0.008;
export const FAKE_BLOOD_DEPLOYED_ROTATION_X = Math.PI;
export const CANDLE_WORLD_SIZE = 0.34;
export const CANDLE_GROUND_CLEARANCE = 0.004;
export const MAX_ACTIVE_COINS = 6;
export const BAIT_PICKUP_DISTANCE = 3;
export const BAIT_PICKUP_MIN_AIM_DOT = 0.9;
export const MIRROR_THROW_DURATION = 0.82;
export const MIRROR_RETURN_DURATION = 0.16;
// The GLB's own Sketchfab_model matrix already maps the authored +Z mat normal
// to glTF/Babylon +Y. Any additional quarter-turn makes the deployed model
// vertical, so the placement correction must remain the identity rotation.
export const TRAMPOLINE_MODEL_CORRECTION_X = 0;
export const TRAMPOLINE_FALLBACK_SURFACE_OFFSET = .40;
export const BAIT_WORLD_CONFIGS: Readonly<Record<BaitId, BaitWorldConfig>> = {
  coins: { size: COIN_WORLD_SIZE, rotation: new Vector3(Math.PI / 2, 0, 0), groundClearance: 0.008 },
  // Inspected geometry: mirror height is local Y and its reflective front is +X.
  // +90 degrees around Z maps +X to world +Y, so the reflective face lies upward.
  mirror: { size: MIRROR_WORLD_SIZE, rotation: new Vector3(0, 0, Math.PI / 2), groundClearance: MIRROR_GROUND_CLEARANCE },
  // Inspected geometry: the blood pack is broad in local XY and thin on Z.
  // Its embedded GLB node transforms already map that plane onto world XZ with the
  // broad face downward; 180 degrees around X keeps it flat and turns the face up.
  "fake-blood": { size: 0.42, rotation: new Vector3(FAKE_BLOOD_DEPLOYED_ROTATION_X, 0, 0), groundClearance: FAKE_BLOOD_GROUND_CLEARANCE },
  // Inspected GLB: its Sketchfab root converts authored local Z to glTF world +Y,
  // and flameAction.001 animates rotation + scale on the flame node.
  candle: { size: CANDLE_WORLD_SIZE, rotation: Vector3.Zero(), groundClearance: CANDLE_GROUND_CLEARANCE },
  // Inspected GLB: the mat is authored in local XY with depth along Z.
  // -90° maps the authored +Z mat normal to world +Y; +90° inverted it.
  trampoline: { size: 2.4, rotation: new Vector3(TRAMPOLINE_MODEL_CORRECTION_X, 0, 0), groundClearance: 0.02 },
};

export function trampolineSurfaceWorldY(lure:PlacedBait):number{const value=(lure.node.metadata as {trampolineSurfaceWorldY?:number}|null)?.trampolineSurfaceWorldY;return typeof value==="number"?value:lure.position.y+TRAMPOLINE_FALLBACK_SURFACE_OFFSET;}

export function mirrorYawFacingPlayer(placement: Vector3, player: Vector3): number {
  return Math.atan2(placement.z - player.z, player.x - placement.x);
}

export function isSingleUnitBait(type: BaitId): type is Exclude<BaitId, "coins"> { return type !== "coins"; }

export function isRecoverableBaitInAim(lure: PlacedBait, origin: Vector3, forward: Vector3, maximumDistance = BAIT_PICKUP_DISTANCE, minimumAimDot = BAIT_PICKUP_MIN_AIM_DOT): boolean {
  if (!isSingleUnitBait(lure.type) || lure.state !== undefined && lure.state !== "GROUND") return false;
  const toLure = lure.position.subtract(origin);
  const distance = toLure.length();
  return distance > 0.001 && distance <= maximumDistance && Vector3.Dot(toLure.scale(1 / distance), forward.normalizeToNew()) >= minimumAimDot;
}

interface MirrorThrowRuntime { readonly lure:PlacedBait;readonly visual:TransformNode;readonly origin:Vector3;readonly velocity:Vector3;readonly baseScale:Vector3;elapsed:number;readonly flightSeconds:number; }

export class LurePlacementSystem {
  readonly lures: PlacedBait[] = [];
  private sequence = 0;
  private readonly preview: TransformNode;
  private readonly previewMaterial: StandardMaterial;
  private readonly candleFlames = new Map<number, TransformNode>();
  private readonly mirrorThrows = new Map<number,MirrorThrowRuntime>();
  private elapsed = 0;

  constructor(
    private readonly scene: Scene,
    private readonly camera: FreeCamera,
    private readonly obstacles: readonly JungleObstacle[],
    private readonly assets: GameAssetManager,
    private readonly onAvailabilityChanged: (availability: BaitAvailability) => void = () => {},
  ) {
    this.preview = new TransformNode("pratonton-umpan", scene);
    const ring = MeshBuilder.CreateTorus("pratonton-umpan-gelung", { diameter: 0.72, thickness: 0.035, tessellation: 24 }, scene);
    ring.parent = this.preview; ring.rotation.x = Math.PI / 2; ring.isPickable = false;
    const marker = MeshBuilder.CreateCylinder("pratonton-umpan-penanda", { diameter: 0.18, height: 0.03, tessellation: 16 }, scene);
    marker.parent = this.preview; marker.isPickable = false;
    this.previewMaterial = this.material("pratonton-umpan-bahan", new Color3(0.35, 1, 0.4)); this.previewMaterial.alpha = 0.68; this.previewMaterial.emissiveColor = new Color3(0.1, 0.5, 0.12);
    ring.material = this.previewMaterial; marker.material = this.previewMaterial; this.preview.setEnabled(false);
  }

  get availability(): BaitAvailability {
    return {
      coins: Infinity,
      mirror: this.lures.some((lure) => lure.type === "mirror") ? 0 : 1,
      "fake-blood": this.lures.some((lure) => lure.type === "fake-blood") ? 0 : 1,
      candle: this.lures.some((lure) => lure.type === "candle") ? 0 : 1,
      trampoline: this.lures.some((lure) => lure.type === "trampoline") ? 0 : 1,
    };
  }
  get mirrorOwnership():import("./LureSystem").MirrorOwnershipState{return this.lures.find((lure)=>lure.type==="mirror")?.mirrorOwnership??"IN_INVENTORY";}

  updatePreview(type: BaitId | undefined): void {
    if (!type) { this.preview.setEnabled(false); return; }
    const candidate = this.getCandidate();
    if (!candidate.point) { this.preview.setEnabled(false); return; }
    this.preview.setEnabled(true); this.preview.position.set(candidate.point.x, candidate.point.y + 0.035, candidate.point.z);
    const available = !isSingleUnitBait(type) || this.availability[type] > 0;
    const color = candidate.valid && available ? new Color3(0.35, 1, 0.4) : new Color3(1, 0.25, 0.15);
    this.previewMaterial.diffuseColor = color; this.previewMaterial.emissiveColor = color.scale(0.35);
  }

  place(type: BaitId): PlacementResult {
    if (isSingleUnitBait(type) && this.availability[type] === 0) return { success: false, message: `${ITEM_DEFINITIONS[type].name} sudah DILETAK. Ambil semula dahulu.` };
    const candidate = this.getCandidate();
    if (!candidate.point || !candidate.valid) return { success: false, message: candidate.message };
    const point = candidate.point;
    if (type === "coins") {
      const coins = this.lures.filter((lure) => lure.type === "coins");
      if (coins.length >= MAX_ACTIVE_COINS) this.removeById(coins[0]!.id, false);
    }
    const config = BAIT_WORLD_CONFIGS[type];
    const root = new TransformNode(`umpan-${type}-${this.sequence}`, this.scene);
    root.position.set(point.x, point.y + config.groundClearance, point.z);
    if(type==="trampoline")root.metadata={trampolineSurfaceWorldY:root.position.y+TRAMPOLINE_FALLBACK_SURFACE_OFFSET,trampolineRadius:1.02};
    if (type === "mirror") root.rotation.y = mirrorYawFacingPlayer(point, this.camera.globalPosition);
    const id = ++this.sequence;
    const flame = this.createFallback(type, root);
    const lure: PlacedBait = { id, type, node: root, position: root.position.clone(), state: "GROUND", mirrorOwnership:type==="mirror"?"PLACED":undefined };
    if (flame) this.candleFlames.set(id, flame);
    this.lures.push(lure);
    const assetPath = ITEM_DEFINITIONS[type].assetPath;
    if (assetPath) void this.attachAsset(assetPath, type, root);
    this.onAvailabilityChanged?.(this.availability);
    return { success: true, message: `${ITEM_DEFINITIONS[type].name} diletakkan. Tunggu dan perhatikan...`, baitId: lure.id, type };
  }

  update(deltaSeconds: number): void {
    this.elapsed += deltaSeconds;
    for (const lure of this.lures) {
      if ((lure.state === "PICKING_UP" || lure.state === "HELD_BY_GHOST") && lure.heldVisual && !lure.heldVisual.isDisposed()) lure.position.copyFrom(lure.heldVisual.getAbsolutePosition());
      const flame = this.candleFlames.get(lure.id);
      if (flame && !flame.isDisposed()) { const pulse = 0.88 + Math.sin(this.elapsed * 13 + lure.id) * 0.12; flame.scaling.set(pulse, 0.92 + pulse * 0.13, pulse); flame.rotation.y += deltaSeconds * 1.8; }
    }
    for(const runtime of [...this.mirrorThrows.values()])this.updateMirrorThrow(runtime,deltaSeconds);
  }

  holdByGhost(lure: PlacedBait, runtimeId: string, attachment: TransformNode): boolean {
    if (lure.type !== "mirror" || lure.state !== "GROUND" || !this.lures.includes(lure)) return false;
    lure.state = "PICKING_UP";lure.mirrorOwnership="PICKING_UP"; lure.holderRuntimeId = runtimeId; lure.node.setEnabled(false);
    lure.position.copyFrom(attachment.getAbsolutePosition());
    void this.attachHeldMirror(lure, attachment);
    this.onAvailabilityChanged?.(this.availability);
    return true;
  }

  completeGhostPickup(id:number,runtimeId:string):boolean{
    const lure=this.lures.find((entry)=>entry.id===id);
    if(!lure||lure.type!=="mirror"||lure.state!=="PICKING_UP"||lure.holderRuntimeId!==runtimeId)return false;
    lure.state="HELD_BY_GHOST";lure.mirrorOwnership="HELD_BY_PONTIANAK";this.onAvailabilityChanged?.(this.availability);return true;
  }

  throwHeldBait(id:number,targetPosition:Vector3):boolean{
    const lure=this.lures.find((entry)=>entry.id===id);
    if(!lure||lure.type!=="mirror"||!(lure.state==="PICKING_UP"||lure.state==="HELD_BY_GHOST"))return false;
    const visual=lure.heldVisual&&!lure.heldVisual.isDisposed()?lure.heldVisual:lure.node;
    const origin=lure.heldVisual&&!lure.heldVisual.isDisposed()?lure.heldVisual.getAbsolutePosition().clone():lure.position.clone();
    if(visual===lure.node)visual.setEnabled(true);
    else{
      const scale=Vector3.One(),rotation=Quaternion.Identity(),translation=Vector3.Zero();visual.computeWorldMatrix(true);visual.getWorldMatrix().decompose(scale,rotation,translation);visual.setParent(null);visual.position.copyFrom(translation);visual.scaling.copyFrom(scale);visual.rotationQuaternion=rotation;
    }
    let direction=targetPosition.subtract(origin);direction.y=0;if(direction.lengthSquared()<.001)direction.set(0,0,1);direction.normalize();
    const variation=(Math.sin(id*17.17)*.5)*.18;const cos=Math.cos(variation),sin=Math.sin(variation);direction.set(direction.x*cos-direction.z*sin,0,direction.x*sin+direction.z*cos);
    const maximumDistance=this.clipThrowDistance(origin,direction);const flightSeconds=Math.max(.45,Math.min(MIRROR_THROW_DURATION,maximumDistance/4.6));
    lure.state="THROWN";lure.mirrorOwnership="THROWN";lure.holderRuntimeId=undefined;lure.position.copyFrom(origin);
    this.mirrorThrows.set(id,{lure,visual,origin,velocity:new Vector3(direction.x*4.6,2.15,direction.z*4.6),baseScale:visual.scaling.clone(),elapsed:0,flightSeconds});
    this.onAvailabilityChanged?.(this.availability);return true;
  }

  returnMirrorToInventory(id:number):boolean{
    const lure=this.lures.find((entry)=>entry.id===id);if(!lure||lure.type!=="mirror")return false;
    this.removeById(id,true);return true;
  }

  releaseHeldBait(id: number, requestedPosition: Vector3): void {
    const lure = this.lures.find((entry) => entry.id === id);
    if (!lure || !(lure.state === "PICKING_UP" || lure.state === "HELD_BY_GHOST")) return;
    lure.heldVisual?.dispose(); lure.heldVisual = undefined; lure.holderRuntimeId = undefined; lure.state = "GROUND";lure.mirrorOwnership=lure.type==="mirror"?"PLACED":undefined;
    const point = this.findSafeReleasePoint(requestedPosition);
    const config = BAIT_WORLD_CONFIGS[lure.type];
    lure.position.set(point.x, point.y + config.groundClearance, point.z);
    lure.node.position.copyFrom(lure.position); lure.node.setEnabled(true);
    this.onAvailabilityChanged?.(this.availability);
  }

  findRecoverableInAim(origin = this.camera.globalPosition, forward = this.camera.getDirection(Vector3.Forward())): PlacedBait | undefined {
    return this.lures.filter((lure) => isRecoverableBaitInAim(lure, origin, forward)).filter((lure) => this.hasClearPickupLine(origin, lure)).sort((a, b) => Vector3.DistanceSquared(a.position, origin) - Vector3.DistanceSquared(b.position, origin))[0];
  }

  retrieveAimed(origin = this.camera.globalPosition, forward = this.camera.getDirection(Vector3.Forward())): PlacementResult | undefined {
    const lure = this.findRecoverableInAim(origin, forward);
    if (!lure) return undefined;
    this.removeById(lure.id, true);
    return { success: true, message: `${ITEM_DEFINITIONS[lure.type].name} diambil semula.`, baitId: lure.id, type: lure.type };
  }

  clearEncounter(): number {
    const count = this.lures.length;
    for (const lure of this.lures) { lure.heldVisual?.dispose(); lure.node.dispose(); }
    this.lures.length = 0;this.mirrorThrows?.clear();
    this.candleFlames?.clear();
    this.onAvailabilityChanged?.(this.availability);
    return count;
  }

  clearType(type:BaitId):number{
    const ids=this.lures.filter((lure)=>lure.type===type).map((lure)=>lure.id);
    for(const id of ids)this.removeById(id,false);
    if(ids.length>0)this.onAvailabilityChanged?.(this.availability);
    return ids.length;
  }

  dispose(): void { this.clearEncounter(); this.preview.dispose(); }

  private hasClearPickupLine(origin: Vector3, lure: PlacedBait): boolean {
    const target = lure.position.add(new Vector3(0, 0.04, 0));
    const direction = target.subtract(origin); const distance = direction.length();
    if (distance <= 0.001) return true;
    const obstruction = this.scene.pickWithRay(new Ray(origin, direction.scale(1 / distance), Math.max(0, distance - 0.08)), (mesh) => mesh.metadata?.worldObstacle === true);
    return !obstruction?.hit;
  }

  private removeById(id: number, notify: boolean): void {
    const index = this.lures.findIndex((lure) => lure.id === id); if (index < 0) return;
    const [removed] = this.lures.splice(index, 1);const runtime=this.mirrorThrows.get(id);if(runtime&&runtime.visual!==removed?.heldVisual&&runtime.visual!==removed?.node)runtime.visual.dispose();this.mirrorThrows.delete(id);removed?.heldVisual?.dispose(); removed?.node.dispose();this.candleFlames.delete(id);
    if (notify) this.onAvailabilityChanged?.(this.availability);
  }

  private getCandidate(): { point?: Vector3; valid: boolean; message: string } {
    const forward = this.camera.getDirection(Vector3.Forward());
    const ray = new Ray(this.camera.globalPosition, forward, 8);
    const hit = this.scene.pickWithRay(ray, (mesh) => mesh.metadata?.ground === true);
    if (!hit?.hit || !hit.pickedPoint) return { valid: false, message: "Halakan retikel ke tanah yang berdekatan." };
    const point = hit.pickedPoint;
    if (Math.abs(point.x) > 28 || Math.abs(point.z) > 28) return { point, valid: false, message: "Umpan mesti diletakkan di dalam kawasan rondaan." };
    const blocked = this.obstacles.some((obstacle) => Vector3.DistanceSquared(new Vector3(point.x, 0, point.z), obstacle.position) < (obstacle.radius + 0.7) ** 2);
    return blocked ? { point, valid: false, message: "Terlalu dekat dengan pokok. Cari ruang lapang." } : { point, valid: true, message: "" };
  }

  private createFallback(type: BaitId, root: TransformNode): TransformNode | undefined {
    if (type === "coins") {
      const gold = this.material("emas", new Color3(0.95, 0.68, 0.08));
      for (let i = 0; i < 4; i++) {
        const coin = MeshBuilder.CreateCylinder(`syiling-${i}`, { diameter: COIN_WORLD_SIZE, height: 0.012, tessellation: 18 }, this.scene);
        coin.parent = root; coin.material = gold; coin.position.set((i % 2 - 0.5) * 0.07, i * 0.004, (Math.floor(i / 2) - 0.5) * 0.07); coin.metadata = { fallbackBait: true };
      }
    } else if (type === "mirror") {
      const mirror = MeshBuilder.CreateDisc("cermin-fallback", { radius: 0.27, tessellation: 24 }, this.scene);
      mirror.parent = root; mirror.position.y = 0.008; mirror.rotation.x = -Math.PI / 2; mirror.material = this.material("cermin", new Color3(0.55, 0.78, 0.88)); mirror.metadata = { fallbackBait: true };
    } else if (type === "fake-blood") {
      const packet = MeshBuilder.CreateBox("paket-darah-palsu", { width: 0.3, height: 0.045, depth: 0.22 }, this.scene);
      packet.parent = root; packet.position.y = 0.0225; packet.material = this.material("paket-darah-merah", new Color3(0.55, 0.025, 0.035)); packet.metadata = { fallbackBait: true };
    } else if(type === "candle") {
      const body = MeshBuilder.CreateCylinder("lilin-fallback", { height: 0.28, diameter: 0.09, tessellation: 14 }, this.scene);
      body.parent = root; body.position.y = 0.14; body.material = this.material("lilin-wax", new Color3(0.92, 0.78, 0.45)); body.metadata = { fallbackBait: true };
      const flame = MeshBuilder.CreateSphere("api-lilin-fallback", { diameter: 0.075, segments: 7 }, this.scene);
      flame.parent = root; flame.position.y = 0.34; flame.scaling.y = 1.65; const flameMaterial = this.material("api-lilin", new Color3(1, 0.48, 0.06));flameMaterial.emissiveColor=new Color3(0.72,0.18,0.015);flame.material=flameMaterial;flame.metadata={fallbackBait:true};
      return flame;
    } else {
      const mat=MeshBuilder.CreateCylinder("trampoline-fallback",{diameter:2.1,height:.12,tessellation:24},this.scene);mat.parent=root;mat.position.y=.34;mat.material=this.material("trampoline-mat",new Color3(.08,.18,.17));mat.metadata={fallbackBait:true};
      for(let index=0;index<4;index++){const leg=MeshBuilder.CreateCylinder(`trampoline-leg-${index}`,{height:.62,diameter:.07,tessellation:8},this.scene);const angle=index*Math.PI/2+Math.PI/4;leg.parent=root;leg.position.set(Math.cos(angle)*.82,.12,Math.sin(angle)*.82);leg.material=this.material("trampoline-frame",new Color3(.5,.55,.52));leg.metadata={fallbackBait:true};}
    }
    return undefined;
  }

  private async attachAsset(path: string, type: BaitId, root: TransformNode): Promise<void> {
    const config = BAIT_WORLD_CONFIGS[type];
    const asset = await this.assets.instantiate(path, `glb-${type}`, { scale: config.size, rotation: config.rotation, playAnimations: type === "candle" });
    if (!asset || root.isDisposed()) { asset?.dispose(); return; }
    asset.parent = root;
    root.computeWorldMatrix(true);asset.computeWorldMatrix(true);
    const meshes=asset.getChildMeshes(false);for(const mesh of meshes)mesh.computeWorldMatrix(true);
    const minimumY=meshes.reduce((minimum,mesh)=>Math.min(minimum,mesh.getBoundingInfo().boundingBox.minimumWorld.y),Number.POSITIVE_INFINITY);
    if(Number.isFinite(minimumY)){asset.position.y+=root.getAbsolutePosition().y-minimumY;asset.computeWorldMatrix(true);for(const mesh of meshes)mesh.computeWorldMatrix(true);}
    if(type==="trampoline"){const maximumY=meshes.reduce((maximum,mesh)=>Math.max(maximum,mesh.getBoundingInfo().boundingBox.maximumWorld.y),Number.NEGATIVE_INFINITY);if(Number.isFinite(maximumY))root.metadata={...root.metadata,trampolineSurfaceWorldY:maximumY,trampolineRadius:1.02};}
    for (const mesh of root.getChildMeshes(true)) if (mesh.metadata?.fallbackBait === true) mesh.setEnabled(false);
  }

  private async attachHeldMirror(lure: PlacedBait, attachment: TransformNode): Promise<void> {
    const visual = await this.assets.instantiate(ITEM_DEFINITIONS.mirror.assetPath!, `cermin-dipegang-${lure.id}`, { scale: 0.38, rotation: new Vector3(0, Math.PI / 2, 0) });
    if (!visual || !(lure.state === "PICKING_UP" || lure.state === "HELD_BY_GHOST") || !this.lures.includes(lure) || attachment.isDisposed()) { visual?.dispose(); return; }
    visual.parent = attachment; visual.position.set(0, 0, 0); visual.rotation.z = -0.12; lure.heldVisual = visual;
  }

  private updateMirrorThrow(runtime:MirrorThrowRuntime,deltaSeconds:number):void{
    if(!this.mirrorThrows.has(runtime.lure.id)||runtime.visual.isDisposed())return;
    runtime.elapsed+=Math.max(0,deltaSeconds);
    if(runtime.elapsed<runtime.flightSeconds){
      const t=runtime.elapsed;const position=runtime.origin.add(runtime.velocity.scale(t));position.y-=4.2*t*t*.5;runtime.visual.position.copyFrom(position);runtime.visual.rotation.x+=deltaSeconds*7.2;runtime.visual.rotation.y+=deltaSeconds*10.5;runtime.visual.rotation.z+=deltaSeconds*5.8;runtime.lure.position.copyFrom(position);return;
    }
    runtime.lure.state="RETURNING";runtime.lure.mirrorOwnership="RETURNING";const returnT=Math.min(1,(runtime.elapsed-runtime.flightSeconds)/MIRROR_RETURN_DURATION);runtime.visual.scaling.copyFrom(runtime.baseScale.scale(Math.max(.01,1-returnT)));
    if(returnT>=1)this.removeById(runtime.lure.id,true);
  }

  private clipThrowDistance(origin:Vector3,direction:Vector3):number{
    let distance=3.8;
    for(const obstacle of this.obstacles){const offset=obstacle.position.subtract(origin);offset.y=0;const along=Vector3.Dot(offset,direction);if(along<=0||along>=distance)continue;const closest=origin.add(direction.scale(along));if(Vector3.DistanceSquared(new Vector3(closest.x,0,closest.z),new Vector3(obstacle.position.x,0,obstacle.position.z))<(obstacle.radius+.3)**2)distance=Math.max(.8,along-obstacle.radius-.35);}
    return distance;
  }

  private findSafeReleasePoint(requested: Vector3): Vector3 {
    const valid = (point: Vector3) => Math.abs(point.x) <= 28 && Math.abs(point.z) <= 28 && !this.obstacles.some((obstacle) => Vector3.DistanceSquared(point, obstacle.position) < (obstacle.radius + 0.72) ** 2);
    const base = new Vector3(requested.x, 0, requested.z); if (valid(base)) return base;
    for (let index = 0; index < 16; index++) { const radius = index < 8 ? 1.2 : 2; const point = base.add(new Vector3(Math.cos(index * Math.PI / 4) * radius, 0, Math.sin(index * Math.PI / 4) * radius)); if (valid(point)) return point; }
    return new Vector3(0, 0, 0);
  }

  private material(name: string, color: Color3): StandardMaterial {
    const material = new StandardMaterial(name, this.scene); material.diffuseColor = color; material.specularColor = Color3.White().scale(0.6); return material;
  }
}
